/**
 * IcpSettings — configure the Ideal Customer Profile for outreach targeting.
 *
 * Fetches current ICP on mount and saves via PUT /api/settings/icp.
 * All state is immutable; handlers derive new objects from previous state.
 */

import { useState, useCallback, useEffect, useMemo, useRef, forwardRef, useImperativeHandle } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Loader2, CheckCircle2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { ChipInput } from '@/components/chip-input'
import { LocationChipInput } from '@/components/location-chip-input'
import { useApi } from '@/hooks/use-api'
import { INDUSTRIES } from '@dealcloser/shared'
import { industryChips } from './industry-chips'

// ============================================================
// Types
// ============================================================

const COMPANY_SIZES = ['1-10', '11-50', '51-200', '201-500', '500+'] as const
type CompanySize = (typeof COMPANY_SIZES)[number]

type IcpConfig = {
  readonly jobTitles: readonly string[]
  readonly industries: readonly string[]
  readonly companySizes: readonly CompanySize[]
  readonly locations: readonly string[]
  readonly industryKeywords: string
  readonly competitorUrls: readonly string[]
}

const DEFAULT_ICP: IcpConfig = {
  jobTitles: [],
  industries: [],
  companySizes: [],
  locations: [],
  industryKeywords: '',
  competitorUrls: ['', '', '', '', ''],
}

const icpKeys = { config: ['settings', 'icp'] as const }

// ============================================================
// Component
// ============================================================

type IcpSettingsProps = {
  readonly defaultValues?: Partial<IcpConfig>
}

/** Handle exposed via ref for programmatic save (e.g. onboarding wizard). */
export type IcpSettingsHandle = {
  save: () => Promise<boolean>
}

const IcpSettings = forwardRef<IcpSettingsHandle, IcpSettingsProps>(function IcpSettings(
  { defaultValues } = {},
  ref,
) {
  const queryClient = useQueryClient()
  const apiFetch = useApi()
  const [form, setForm] = useState<IcpConfig>(DEFAULT_ICP)
  const [savedSuccess, setSavedSuccess] = useState(false)
  const defaultsAppliedRef = useRef(false)

  const { data, isLoading } = useQuery({
    queryKey: icpKeys.config,
    queryFn: async (): Promise<IcpConfig> => {
      const res = await apiFetch<IcpConfig>('/api/settings/icp')
      if (!res.success || !res.data) return DEFAULT_ICP
      return res.data
    },
    staleTime: 5 * 60_000,
  })

  useEffect(() => {
    if (!data) return
    const hasExistingData = data.jobTitles.length > 0 || data.industries.length > 0
    if (!hasExistingData && defaultValues && !defaultsAppliedRef.current) {
      // Merge AI-generated defaults into the empty server data (one-time)
      defaultsAppliedRef.current = true
      setForm({ ...DEFAULT_ICP, ...data, ...defaultValues })
    } else if (!defaultsAppliedRef.current) {
      // Populate from server data only if we haven't applied AI defaults
      setForm(data)
    }
  }, [data, defaultValues])

  const saveMutation = useMutation({
    mutationFn: async (config: IcpConfig): Promise<void> => {
      const res = await apiFetch('/api/settings/icp', { method: 'PUT', body: config })
      if (!res.success) throw new Error(res.error ?? 'Failed to save')
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: icpKeys.config })
      setSavedSuccess(true)
      setTimeout(() => setSavedSuccess(false), 3000)
    },
  })

  const setJobTitles = useCallback((jobTitles: readonly string[]) => {
    setForm((prev) => ({ ...prev, jobTitles }))
  }, [])

  const setLocations = useCallback((locations: readonly string[]) => {
    setForm((prev) => ({ ...prev, locations }))
  }, [])

  const industryOptions = useMemo(
    () => industryChips(INDUSTRIES, data?.industries, defaultValues?.industries, form.industries),
    [data, defaultValues, form.industries],
  )

  function toggleIndustry(industry: string) {
    const next = form.industries.includes(industry)
      ? form.industries.filter((i) => i !== industry)
      : [...form.industries, industry]
    setForm((prev) => ({ ...prev, industries: next }))
  }

  function toggleSize(size: CompanySize) {
    const next = form.companySizes.includes(size)
      ? form.companySizes.filter((s) => s !== size)
      : [...form.companySizes, size]
    setForm((prev) => ({ ...prev, companySizes: next }))
  }

  function setCompetitorUrl(index: number, value: string) {
    const next = form.competitorUrls.map((url, i) => (i === index ? value : url))
    setForm((prev) => ({ ...prev, competitorUrls: next }))
  }

  const isSaving = saveMutation.isPending
  const saveError = saveMutation.error instanceof Error ? saveMutation.error.message : null

  // Expose save() so parent components (e.g. onboarding wizard) can trigger a save programmatically.
  useImperativeHandle(ref, () => ({
    save: () =>
      new Promise<boolean>((resolve) => {
        saveMutation.mutate(form, {
          onSuccess: () => resolve(true),
          onError: () => resolve(false),
        })
      }),
  }), [form, saveMutation])

  return (
    <div className="space-y-6">
      {/* Target Audience */}
      <Card>
        <CardHeader>
          <CardTitle>Target Audience</CardTitle>
          <CardDescription>Define who you want to reach with your outreach campaigns.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <ChipInput
            label="Target Job Titles"
            placeholder="CEO, Head of Sales…"
            chips={form.jobTitles}
            onChange={setJobTitles}
            disabled={isLoading || isSaving}
          />

          {/* Industries */}
          <div className="space-y-2">
            <Label>Target Industries</Label>
            <div className="flex flex-wrap gap-2">
              {industryOptions.map((industry) => (
                <button
                  key={industry}
                  type="button"
                  onClick={() => toggleIndustry(industry)}
                  disabled={isLoading || isSaving}
                  className={[
                    'rounded-full border px-3 py-1 text-sm transition-colors',
                    form.industries.includes(industry)
                      ? 'border-cobalt-bright bg-primary text-primary-foreground'
                      : 'border-input bg-transparent text-foreground hover:bg-muted',
                  ].join(' ')}
                >
                  {industry}
                </button>
              ))}
            </div>
          </div>

          {/* Company size */}
          <div className="space-y-2">
            <Label>Target Company Size (employees)</Label>
            <div className="flex flex-wrap gap-4">
              {COMPANY_SIZES.map((size) => (
                <div key={size} className="flex items-center gap-2">
                  <Checkbox
                    id={`size-${size}`}
                    checked={form.companySizes.includes(size)}
                    onCheckedChange={() => toggleSize(size)}
                    disabled={isLoading || isSaving}
                  />
                  <label htmlFor={`size-${size}`} className="text-sm cursor-pointer">
                    {size}
                  </label>
                </div>
              ))}
            </div>
          </div>

          <LocationChipInput
            label="Target Locations"
            placeholder="Search states, cities, countries..."
            chips={form.locations}
            onChange={setLocations}
            disabled={isLoading || isSaving}
          />
        </CardContent>
      </Card>

      {/* Keywords & Signals */}
      <Card>
        <CardHeader>
          <CardTitle>Keywords & Signals</CardTitle>
          <CardDescription>Help AI identify your best-fit prospects.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <ChipInput
            label="Industry Keywords"
            placeholder="saas, b2b, venture-backed…"
            chips={form.industryKeywords
              .split(',')
              .map((k) => k.trim())
              .filter((k) => k.length > 0)}
            onChange={(chips) =>
              setForm((prev) => ({ ...prev, industryKeywords: chips.join(', ') }))
            }
            disabled={isLoading || isSaving}
          />

          <div className="space-y-3">
            <Label>Competitor LinkedIn URLs (up to 5)</Label>
            {form.competitorUrls.map((url, i) => (
              <div key={i} className="flex items-center gap-2">
                <Input
                  type="url"
                  placeholder={`https://linkedin.com/company/competitor-${i + 1}`}
                  value={url}
                  onChange={(e) => setCompetitorUrl(i, e.target.value)}
                  disabled={isLoading || isSaving}
                />
              </div>
            ))}
            <p className="text-xs text-muted-foreground">We use these to find leads who engage with competitors.</p>
          </div>
        </CardContent>
      </Card>

      {/* Save */}
      <div className="flex items-center gap-3">
        <Button onClick={() => saveMutation.mutate(form)} disabled={isSaving || isLoading}>
          {isSaving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving…
            </>
          ) : (
            'Save ICP Settings'
          )}
        </Button>
        {savedSuccess && (
          <div className="flex items-center gap-1.5 text-sm text-emerald-300 ">
            <CheckCircle2 className="h-4 w-4" />
            Saved successfully
          </div>
        )}
        {saveError && <p className="text-sm text-destructive">{saveError}</p>}
      </div>
    </div>
  )
})

export default IcpSettings
